import { Check } from 'react-feather'
import { Button, Icon, Modal } from '@/components'

const COPY = {
  paused: {
    title: 'Why is my car paused?',
    heading: 'Reasons charging pauses:',
    reasons: [
      'Optiwatt is waiting for a better time to charge',
      'A utility event may be running on your plan',
      'Your car may be set to be ready at a specific time',
      'Charging resumes on its own, no action needed',
    ],
  },
  charging: {
    title: 'Why is my car charging?',
    heading: 'Reasons charging starts:',
    reasons: [
      'Optiwatt found a good window to charge',
      'Your car needs this time to reach your charge limit',
      'A utility event or your own settings can shift this',
      'You can stop or extend charging at any time',
    ],
  },
}

type Props = {
  open: boolean
  isCharging: boolean
  onClose: () => void
  onManageSettings: () => void
}

export default function WhyStatusModal({
  open,
  isCharging,
  onClose,
  onManageSettings,
}: Props) {
  const copy = isCharging ? COPY.charging : COPY.paused

  return (
    <Modal
      id="charge-forecast-v2-why-modal"
      open={open}
      onClose={onClose}
      hideCloseButton
      // The shared modal's inline maxWidth doesn't reach the dialog card, so
      // cap the 331px design width with a class. The card is content-box, so
      // border-box keeps the padding inside the 331px
      className="!box-border !p-4 !rounded-[20px] !max-w-[331px]"
    >
      {/* The shared modal reserves a 16px title row; this layout has its own */}
      <div className="flex flex-col items-center w-full gap-5 -mt-4">
        <div className="flex justify-end w-full">
          <button
            id="charge-forecast-v2-why-modal-close"
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex items-center justify-center w-6 h-6 p-0 border-0 rounded-full cursor-pointer bg-themed-grey-200"
          >
            <Icon
              name="X"
              color="grey-900"
              size={14}
              strokeWidth={2.5}
              aria-hidden
            />
          </button>
        </div>
        <span className="flex items-center justify-center w-16 h-16 rounded-[10px] bg-themed-blue-100">
          <Icon name="Leaf" color="blue-500" size={32} aria-hidden />
        </span>
        <h2 className="m-0 text-base font-bold text-center text-themed-grey-900">
          {copy.title}
        </h2>
        <div className="box-border w-full p-4 bg-white border border-solid rounded-[10px] border-themed-blue-500">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-bold leading-5 text-themed-blue-500">
              {copy.heading}
            </span>
            <ul className="flex flex-col gap-2 p-0 m-0 list-none">
              {copy.reasons.map((reason) => (
                <li key={reason} className="flex items-start gap-2">
                  <span className="flex shrink-0 w-4 h-4 mt-0.5">
                    {/* Icon name="Check" resolves to the app's filled green
                        check; the plain tick comes from react-feather */}
                    <Check
                      size={16}
                      color="var(--color-blue-500)"
                      strokeWidth={2}
                      aria-hidden
                    />
                  </span>
                  <span className="flex-1 text-sm font-medium leading-5 text-themed-grey-500">
                    {reason}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="flex flex-col w-full gap-4">
          <Button
            id="charge-forecast-v2-why-modal-manage-settings"
            variant="primary-subtle"
            className="w-full"
            onClick={onManageSettings}
          >
            Manage settings
          </Button>
          <Button
            id="charge-forecast-v2-why-modal-done"
            variant="primary"
            className="w-full"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  )
}
